import store from '@/store/';
import AuthDao from '@/data/dao/AuthDao';
import {AuthSession} from '@/data/models/auth-session';
import {getFromStorage, removeFromStorage, saveToStorage} from '@/helpers/storage';

const STORAGE_KEY = 'authSession';
/** Marge (secondes) avant expiration à partir de laquelle on rafraîchit l'access token */
const REFRESH_MARGIN = 60;

type StoredSession = AuthSession & { expiresAt: number };

let session: StoredSession | null = null;
let refreshing: Promise<string> | null = null;

/** Expiration indiquée dans le jeton lui-même (claim exp du JWT), null si illisible */
const tokenExpiresAt = (accessToken: string): number | null => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

/**
 * @param expiresAt expiration connue (session restaurée) ; sans valeur, calculée depuis expiresIn
 *   (session qui vient d'être obtenue de l'API : insensible au décalage d'horloge du poste)
 */
const applySession = (value: AuthSession | null, expiresAt?: number) => {
  if (value) {
    session = {...value, expiresAt: expiresAt ?? Date.now() + value.expiresIn * 1000};
    saveToStorage(STORAGE_KEY, session);
    store.commit('setUser', {uid: value.uid, email: value.email});
    store.commit('setIsAdmin', value.isAdmin);
    store.commit('setIsLimited', false);
  } else {
    session = null;
    removeFromStorage(STORAGE_KEY);
    store.commit('setUser', null);
    store.commit('setIsAdmin', false);
    store.commit('setIsLimited', true);
  }
};

export const isLoggedIn = (): boolean => session !== null;

export const isAdmin = (): boolean => !!session?.isAdmin;

/**
 * Access token courant, rafraîchi si nécessaire. Rejette si la session est invalide (l'utilisateur est déconnecté).
 */
const isFresh = (value: StoredSession | null): boolean => !!value && Date.now() < value.expiresAt - REFRESH_MARGIN * 1000;

/**
 * Session rafraîchie entre-temps par un autre onglet (le stockage est partagé, et l'API révoque
 * le refresh token à chaque rafraîchissement) : on la reprend plutôt que de rafraîchir à nouveau.
 */
const adoptSessionFromOtherTab = (): boolean => {
  const stored: StoredSession | null = getFromStorage(STORAGE_KEY, null);
  if (stored?.refreshToken && stored.refreshToken !== session?.refreshToken && isFresh(stored)) {
    session = stored;
    return true;
  }
  return false;
};

export const getAccessToken = (): Promise<string> => {
  if (!session) {
    return Promise.reject(new Error('not logged in'));
  }

  if (isFresh(session) || adoptSessionFromOtherTab()) {
    return Promise.resolve(session.accessToken);
  }

  if (!refreshing) {
    refreshing = AuthDao.refresh(session.refreshToken)
      .then((fresh) => {
        applySession(fresh);
        return fresh.accessToken;
      })
      .catch((err) => {
        // Refresh token déjà utilisé par un autre onglet : reprendre la session qu'il a obtenue
        if (adoptSessionFromOtherTab()) {
          return session.accessToken;
        }
        applySession(null);
        throw err;
      })
      .finally(() => {
        refreshing = null;
      });
  }

  return refreshing;
};

/**
 * Connexion locale.
 * @returns isAdmin
 */
export const signIn = async (email: string, password: string): Promise<boolean> => {
  const result = await AuthDao.login(email, password);
  applySession(result);
  return result.isAdmin;
};

export const signOut = (): Promise<void> => {
  const refreshToken = session?.refreshToken;
  applySession(null);
  return refreshToken ? AuthDao.logout(refreshToken).catch(() => undefined) : Promise.resolve();
};

export const forgotPassword = (email: string): Promise<void> => AuthDao.forgotPassword(email);

export const resetPassword = (token: string, password: string): Promise<void> => AuthDao.resetPassword(token, password);

/**
 * Restaure la session depuis le stockage au démarrage, puis signale que l'application est prête.
 */
export const restoreSession = async (): Promise<void> => {
  const stored: StoredSession | null = getFromStorage(STORAGE_KEY, null);
  if (stored?.refreshToken) {
    // Expiration conservée (et non recalculée depuis maintenant, ce qui la repoussait à chaque rechargement),
    // bornée par celle du jeton lui-même
    const expiresAt = Math.min(stored.expiresAt || 0, tokenExpiresAt(stored.accessToken) ?? Number.POSITIVE_INFINITY);
    session = {...stored, expiresAt};
    try {
      // Valide la session côté API (et rafraîchit si besoin) ; en cas d'échec l'utilisateur est déconnecté.
      if (Date.now() >= expiresAt - REFRESH_MARGIN * 1000) {
        await getAccessToken();
      } else {
        applySession(stored, expiresAt);
      }
    } catch (e) {
      applySession(null);
    }
  }
  store.commit('applicationStarted');
};
